import { useMemo } from 'react';
import { Field } from 'react-final-form';

import { ReservationRequiredField, Gender } from '../generated/graphql';
import { parseText } from '../utilities/parsers';
import {
  composeValidators,
  StringValidatorFunction,
  validDODID,
} from '../utilities/validators';

import FormDropdown from './Inputs/FormInputs/FormDropdown';
import TextField from './Inputs/FormInputs/TextField';

type FieldValidator = (
  value: string | null,
  allValues?: object,
) => string | undefined;

interface FieldConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
  parse?: typeof parseText;
  validation?: StringValidatorFunction | StringValidatorFunction[];
  hints?: Array<{ id: string; text: string }>;
  getExtraProps?: (editMode: boolean) => Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderOption?: (option: any) => React.ReactNode;
}

export interface RequiredFieldItemProps {
  name: string;
  label: string;
  fieldType?: ReservationRequiredField;
  required?: boolean;
  validation?: StringValidatorFunction | StringValidatorFunction[];
  className?: string;
  disabled?: boolean;
}

const TEXT_FIELDS = [
  ReservationRequiredField.TaskForce,
  ReservationRequiredField.Mission,
  ReservationRequiredField.EmployeeNumber,
  ReservationRequiredField.JobTitle,
];

const DEFAULT_CONFIG: FieldConfig = {
  component: TextField,
  parse: parseText,
};

const FIELD_CONFIGS: Partial<Record<ReservationRequiredField, FieldConfig>> = {
  [ReservationRequiredField.Gender]: {
    component: FormDropdown,
    options: [Gender.M, Gender.F],
    renderOption: (option: string) => {
      return option === Gender.M ? 'Male' : 'Female';
    },
  },

  [ReservationRequiredField.Rank]: {
    component: TextField,
    parse: parseText,
  },

  [ReservationRequiredField.DodId]: {
    component: TextField,
    parse: parseText,
    validation: validDODID,
  },

  [ReservationRequiredField.ControlNumber]: {
    component: TextField,
    parse: parseText,
  },

  ...TEXT_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field]: {
        component: TextField,
        parse: parseText,
      },
    }),
    {},
  ),
};

export default function RequiredFieldItem({
  name,
  label,
  fieldType,
  required: isRequired,
  validation: propValidation,
  className = '',
  disabled = false,
}: RequiredFieldItemProps) {
  const config = fieldType
    ? FIELD_CONFIGS[fieldType] || DEFAULT_CONFIG
    : DEFAULT_CONFIG;

  const validation = useMemo<FieldValidator | undefined>(() => {
    const validationToUse = propValidation || config.validation;
    if (!validationToUse) return undefined;

    if (Array.isArray(validationToUse)) {
      return composeValidators(...validationToUse);
    }

    return validationToUse as FieldValidator;
  }, [propValidation, config.validation]);

  if (!fieldType) {
    return null;
  }

  const extraProps = config.getExtraProps ? config.getExtraProps(false) : {};

  return (
    <div className={`mb-4 ${className}`}>
      <Field
        name={name}
        labelText={label}
        component={config.component}
        parse={config.parse}
        validate={validation}
        required={isRequired}
        disabled={disabled}
        options={config.options}
        {...(config.renderOption && { renderOption: config.renderOption })}
        {...extraProps}
      />

      {config.hints?.map((hint) => (
        <p key={hint.id} id={hint.id} className="mt-1 text-sm text-gray-500">
          {hint.text}
        </p>
      ))}
    </div>
  );
}
